// src/components/workout/CustomExercisesManager.jsx
import React, { useState, useEffect, useMemo } from 'react'; // Added useMemo
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { supabase } from '../../lib/supabase';
import { useToast } from "../ui/use-toast";
import { ScrollArea } from "../ui/scroll-area";
import { Search, Plus, Edit, Trash2, Dumbbell, Copy } from 'lucide-react';

const CustomExercisesManager = ({ userId }) => {
  const [exercises, setExercises] = useState([]); // User's custom exercises
  const [publicExercises, setPublicExercises] = useState([]); // Public exercises
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all'); // 'all', 'custom', 'public' - NEW STATE
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [currentExercise, setCurrentExercise] = useState(null);
  const { toast } = useToast();

  // Default new exercise template
  const emptyExercise = {
    name: '',
    description: '',
    exercise_type: 'barbell',
    default_sets: 3,
    default_reps: 8,
    default_rep_min: 8,
    default_rep_max: 12,
    default_rep_type: 'static',
    default_rest_time: 90,
    default_increment: 5
  };

  // Exercise types
  const exerciseTypes = [
    { value: 'barbell', label: 'Barbell' },
    { value: 'dumbbell', label: 'Dumbbell' },
    { value: 'machine', label: 'Machine' },
    { value: 'cable', label: 'Cable' },
    { value: 'bodyweight', label: 'Bodyweight' },
    { value: 'other', label: 'Other' }
  ];

  // Rep types
  const repTypes = [
    { value: 'static', label: 'Static (fixed reps)' },
    { value: 'dynamic', label: 'Dynamic (rep range)' }
  ];

  // Load exercises (custom and public)
  useEffect(() => {
    const fetchExercises = async () => {
      if (!userId) return;
      setLoading(true);

      try {
        // Fetch user's custom exercises
        const { data: customData, error: customError } = await supabase
          .from('custom_exercises')
          .select('*')
          .eq('user_id', userId)
          .order('name');

        if (customError) throw customError;
        setExercises(customData || []);

        // Fetch public exercises
        const { data: publicData, error: publicError } = await supabase
          .from('public_exercises')
          .select('*')
          .order('name');

        if (publicError) throw publicError;
        setPublicExercises(publicData || []);

      } catch (error) {
        console.error('Error fetching exercises:', error);
        toast({
          title: 'Error',
          description: 'Failed to load exercises',
          variant: 'destructive'
        });
        setExercises([]); // Ensure state is empty on error
        setPublicExercises([]);
      } finally {
        setLoading(false);
      }
    };

    fetchExercises();
  }, [userId, toast]);

  // *** NEW: Memoized list based on filter type ***
  const baseList = useMemo(() => {
    if (filterType === 'custom') {
      return exercises.map(ex => ({ ...ex, isPublic: false }));
    } else if (filterType === 'public') {
      return publicExercises.map(e => ({ ...e, isPublic: true }));
    } else { // 'all'
      return [
        ...exercises.map(ex => ({ ...ex, isPublic: false })),
        ...publicExercises.map(e => ({ ...e, isPublic: true }))
      ];
    }
  }, [exercises, publicExercises, filterType]);

  // *** NEW: Memoized list based on search query applied to baseList ***
  const filteredExercises = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (query === '') {
      return baseList;
    }
    return baseList.filter(exercise =>
      exercise.name.toLowerCase().includes(query) ||
      (exercise.description && exercise.description.toLowerCase().includes(query)) ||
      exercise.exercise_type.toLowerCase().includes(query)
    );
  }, [baseList, searchQuery]);

  // Handle adding a new exercise
  const handleAddExercise = async () => {
    if (!currentExercise?.name?.trim()) {
      toast({ title: 'Name Required', description: 'Please provide a name for the exercise', variant: 'destructive' });
      return;
    }
    setSubmitting(true);
    try {
      const exerciseData = {
        name: currentExercise.name,
        description: currentExercise.description || null,
        exercise_type: currentExercise.exercise_type,
        default_sets: parseInt(currentExercise.default_sets, 10) || emptyExercise.default_sets,
        default_reps: parseInt(currentExercise.default_reps, 10) || emptyExercise.default_reps,
        default_rep_min: parseInt(currentExercise.default_rep_min, 10) || emptyExercise.default_rep_min,
        default_rep_max: parseInt(currentExercise.default_rep_max, 10) || emptyExercise.default_rep_max,
        default_rep_type: currentExercise.default_rep_type,
        default_rest_time: parseInt(currentExercise.default_rest_time, 10) || emptyExercise.default_rest_time,
        default_increment: parseFloat(currentExercise.default_increment) || emptyExercise.default_increment,
        user_id: userId
      };

      if (exerciseData.default_rep_type === 'dynamic' && exerciseData.default_rep_min > exerciseData.default_rep_max) {
           [exerciseData.default_rep_min, exerciseData.default_rep_max] = [exerciseData.default_rep_max, exerciseData.default_rep_min];
           toast({ title: 'Rep Range Adjusted', description: 'Minimum reps cannot exceed maximum reps. Values have been swapped.' });
      }

      const { data, error } = await supabase
        .from('custom_exercises')
        .insert(exerciseData)
        .select()
        .single();

      if (error) throw error;

      setExercises(prev => [...prev, data].sort((a, b) => a.name.localeCompare(b.name))); // Add and sort
      setShowAddDialog(false);
      setCurrentExercise(null);
      toast({ title: 'Exercise Added', description: `${data.name} has been added to your custom exercises` });
    } catch (error) {
      console.error('Error adding exercise:', error);
      toast({ title: 'Error', description: `Failed to add exercise: ${error.message}`, variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  // Handle updating an exercise
  const handleUpdateExercise = async () => {
    if (!currentExercise?.name?.trim()) {
      toast({ title: 'Name Required', description: 'Please provide a name for the exercise', variant: 'destructive' });
      return;
    }
    if (!currentExercise?.id) {
        toast({ title: 'Error', description: 'Cannot update exercise: Missing ID.', variant: 'destructive'});
        return;
    }
    setSubmitting(true);
    try {
        const updateData = {
            name: currentExercise.name,
            description: currentExercise.description || null,
            exercise_type: currentExercise.exercise_type,
            default_sets: parseInt(currentExercise.default_sets, 10) || emptyExercise.default_sets,
            default_reps: parseInt(currentExercise.default_reps, 10) || emptyExercise.default_reps,
            default_rep_min: parseInt(currentExercise.default_rep_min, 10) || emptyExercise.default_rep_min,
            default_rep_max: parseInt(currentExercise.default_rep_max, 10) || emptyExercise.default_rep_max,
            default_rep_type: currentExercise.default_rep_type,
            default_rest_time: parseInt(currentExercise.default_rest_time, 10) || emptyExercise.default_rest_time,
            default_increment: parseFloat(currentExercise.default_increment) || emptyExercise.default_increment,
            updated_at: new Date().toISOString()
        };

        if (updateData.default_rep_type === 'dynamic' && updateData.default_rep_min > updateData.default_rep_max) {
            [updateData.default_rep_min, updateData.default_rep_max] = [updateData.default_rep_max, updateData.default_rep_min];
            toast({ title: 'Rep Range Adjusted', description: 'Minimum reps cannot exceed maximum reps. Values have been swapped.' });
        }

      const { data, error } = await supabase
        .from('custom_exercises')
        .update(updateData)
        .eq('id', currentExercise.id)
        .eq('user_id', userId)
        .select()
        .single();

      if (error) throw error;

      setExercises(prev => prev.map(ex => ex.id === currentExercise.id ? data : ex)
                                .sort((a, b) => a.name.localeCompare(b.name))); // Update and sort
      setShowEditDialog(false);
      setCurrentExercise(null);
      toast({ title: 'Exercise Updated', description: `${data.name} has been updated` });
    } catch (error) {
      console.error('Error updating exercise:', error);
      toast({ title: 'Error', description: `Failed to update exercise: ${error.message}`, variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  // Handle deleting an exercise
  const handleDeleteExercise = async () => {
    if (!currentExercise?.id) {
        toast({ title: 'Error', description: 'Cannot delete exercise: Missing ID.', variant: 'destructive'});
        return;
    }
    setSubmitting(true);
    try {
      const exerciseNameToDelete = currentExercise.name;
      const { error } = await supabase
        .from('custom_exercises')
        .delete()
        .eq('id', currentExercise.id)
        .eq('user_id', userId);

      if (error) throw error;

      setExercises(prev => prev.filter(ex => ex.id !== currentExercise.id)); // No need to sort after filter
      setShowDeleteDialog(false);
      setCurrentExercise(null);
      toast({ title: 'Exercise Deleted', description: `${exerciseNameToDelete} has been deleted` });
    } catch (error) {
      console.error('Error deleting exercise:', error);
      toast({ title: 'Error', description: `Failed to delete exercise: ${error.message}`, variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  // Handle copying a public exercise to custom
  const handleCopyPublicExercise = (publicExercise) => {
    const newCustomExercise = {
      ...emptyExercise,
      name: `${publicExercise.name} (Copy)`,
      description: publicExercise.description || '',
      exercise_type: publicExercise.exercise_type || 'barbell',
      default_sets: publicExercise.default_sets || emptyExercise.default_sets,
      default_reps: publicExercise.default_reps || emptyExercise.default_reps,
      default_rep_min: publicExercise.default_rep_min || emptyExercise.default_rep_min,
      default_rep_max: publicExercise.default_rep_max || emptyExercise.default_rep_max,
      default_rep_type: publicExercise.default_rep_type || 'static',
      default_rest_time: publicExercise.default_rest_time || emptyExercise.default_rest_time,
      default_increment: publicExercise.default_increment || emptyExercise.default_increment,
    };
    setCurrentExercise(newCustomExercise);
    setShowAddDialog(true);
  };


  // Render exercise form (shared between add and edit)
  const renderExerciseForm = () => (
    <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
      <div className="space-y-2">
        <Label htmlFor="exercise-name">Exercise Name*</Label>
        <Input
          id="exercise-name"
          value={currentExercise?.name || ''}
          onChange={(e) => setCurrentExercise({ ...currentExercise, name: e.target.value })}
          placeholder="e.g., Incline Dumbbell Press"
          required
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="exercise-type">Exercise Type*</Label>
        <Select
          value={currentExercise?.exercise_type || 'barbell'}
          onValueChange={(value) => setCurrentExercise({ ...currentExercise, exercise_type: value })}
        >
          <SelectTrigger id="exercise-type">
            <SelectValue placeholder="Select type" />
          </SelectTrigger>
          <SelectContent>
            {exerciseTypes.map(type => (
              <SelectItem key={type.value} value={type.value}>
                {type.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label htmlFor="exercise-description">Description (Optional)</Label>
        <Textarea
          id="exercise-description"
          value={currentExercise?.description || ''}
          onChange={(e) => setCurrentExercise({ ...currentExercise, description: e.target.value })}
          placeholder="Add notes or instructions for this exercise"
          rows={3}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="rep-type">Rep Type</Label>
        <Select
          value={currentExercise?.default_rep_type || 'static'}
          onValueChange={(value) => setCurrentExercise({ ...currentExercise, default_rep_type: value })}
        >
          <SelectTrigger id="rep-type">
            <SelectValue placeholder="Select rep type" />
          </SelectTrigger>
          <SelectContent>
            {repTypes.map(type => (
              <SelectItem key={type.value} value={type.value}>
                {type.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground">
          Static: Fixed reps per set. Dynamic: Rep range per set.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-4">
         <div className="space-y-2">
            <Label htmlFor="default-sets">Default Sets</Label>
            <Input
                id="default-sets"
                type="number"
                value={currentExercise?.default_sets ?? emptyExercise.default_sets}
                onChange={(e) => setCurrentExercise({
                  ...currentExercise,
                  default_sets: e.target.value ? parseInt(e.target.value, 10) : '' // Allow clearing, handle default on save
                })}
                min={1}
                step={1}
                placeholder={String(emptyExercise.default_sets)}
            />
        </div>

        {currentExercise?.default_rep_type === 'static' ? (
             <div className="space-y-2">
                <Label htmlFor="default-reps">Default Reps (Static)</Label>
                <Input
                  id="default-reps"
                  type="number"
                  value={currentExercise?.default_reps ?? emptyExercise.default_reps}
                  onChange={(e) => setCurrentExercise({
                      ...currentExercise,
                      default_reps: e.target.value ? parseInt(e.target.value, 10) : ''
                  })}
                  min={1}
                  step={1}
                  placeholder={String(emptyExercise.default_reps)}
                />
            </div>
        ) : (
           <>
            <div className="space-y-2">
                <Label htmlFor="min-reps">Min Reps (Dynamic)</Label>
                <Input
                  id="min-reps"
                  type="number"
                  value={currentExercise?.default_rep_min ?? emptyExercise.default_rep_min}
                  onChange={(e) => setCurrentExercise({
                      ...currentExercise,
                      default_rep_min: e.target.value ? parseInt(e.target.value, 10) : ''
                  })}
                  min={1}
                  step={1}
                   placeholder={String(emptyExercise.default_rep_min)}
                />
            </div>
             <div className="space-y-2">
                <Label htmlFor="max-reps">Max Reps (Dynamic)</Label>
                <Input
                  id="max-reps"
                  type="number"
                  value={currentExercise?.default_rep_max ?? emptyExercise.default_rep_max}
                  onChange={(e) => setCurrentExercise({
                      ...currentExercise,
                      default_rep_max: e.target.value ? parseInt(e.target.value, 10) : ''
                  })}
                  min={currentExercise?.default_rep_min || 1}
                  step={1}
                  placeholder={String(emptyExercise.default_rep_max)}
                />
            </div>
           </>
        )}
        </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="rest-time">Rest Time (seconds)</Label>
          <Input
            id="rest-time"
            type="number"
            value={currentExercise?.default_rest_time ?? emptyExercise.default_rest_time}
            onChange={(e) => setCurrentExercise({
              ...currentExercise,
              default_rest_time: e.target.value ? parseInt(e.target.value, 10) : ''
            })}
            min={15}
            step={15}
            placeholder={String(emptyExercise.default_rest_time)}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="default-increment">Weight Increment</Label>
          <Input
            id="default-increment"
            type="number"
            value={currentExercise?.default_increment ?? emptyExercise.default_increment}
            onChange={(e) => setCurrentExercise({
              ...currentExercise,
              default_increment: e.target.value ? parseFloat(e.target.value) : ''
            })}
            min={0.1}
            step={0.1}
            placeholder={String(emptyExercise.default_increment)}
          />
           <p className="text-xs text-muted-foreground">
            Smallest typical weight increase.
           </p>
        </div>
      </div>
    </div>
  );

  // Main Component Return
  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
          <div>
            <CardTitle>Exercise Library</CardTitle>
            <CardDescription>
              Manage custom exercises and browse public options.
            </CardDescription>
          </div>
          <Button onClick={() => {
            setCurrentExercise({ ...emptyExercise });
            setShowAddDialog(true);
          }}>
            <Plus className="h-4 w-4 mr-2" />
            Add Custom Exercise
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {/* Search Bar */}
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search exercises by name, type, or description..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* --- Filter Buttons --- */}
        <div className="flex flex-wrap gap-2 mb-4"> {/* Added flex-wrap */}
          <Button
            variant={filterType === 'all' ? 'secondary' : 'outline'}
            size="sm"
            onClick={() => setFilterType('all')}
          >
            All ({baseList.length}) {/* Show count for all */}
          </Button>
          <Button
            variant={filterType === 'custom' ? 'secondary' : 'outline'}
            size="sm"
            onClick={() => setFilterType('custom')}
            disabled={exercises.length === 0 && !loading} // Disable only if loaded and empty
          >
            My Exercises ({exercises.length})
          </Button>
          <Button
            variant={filterType === 'public' ? 'secondary' : 'outline'}
            size="sm"
            onClick={() => setFilterType('public')}
            disabled={publicExercises.length === 0 && !loading} // Disable only if loaded and empty
          >
            Public ({publicExercises.length})
          </Button>
        </div>
        {/* --- End Filter Buttons --- */}

        {/* Exercise List */}
        {loading ? (
          <div className="text-center py-8">
            <p className="text-muted-foreground">Loading exercises...</p>
            {/* Consider adding a spinner component here */}
          </div>
        ) : filteredExercises.length > 0 ? (
          <ScrollArea className="h-[400px] pr-4 border rounded-md">
            <div className="space-y-2 p-1">
                {/* --- MAPPING OVER EXERCISES --- */}
                {filteredExercises.map((exercise) => (
                <div
                    key={`${exercise.isPublic ? 'pub-' : 'cust-'}${exercise.id}`} // Use safer key
                    className={`flex justify-between items-start p-3 border rounded-lg ${exercise.isPublic ? 'bg-muted/10 border-dashed' : 'bg-card border-border'}`}
                >
                    {/* Exercise Details */}
                    <div className="flex-1 mr-2 overflow-hidden">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                            <h3 className="font-semibold text-sm sm:text-base">{exercise.name}</h3>
                            {exercise.isPublic && (
                            <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full font-medium whitespace-nowrap">
                                Public
                            </span>
                            )}
                            <span className="text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded-md whitespace-nowrap">
                                {exerciseTypes.find(t => t.value === exercise.exercise_type)?.label || exercise.exercise_type}
                            </span>
                        </div>
                        {exercise.description && (
                            <p className="text-xs sm:text-sm text-muted-foreground mt-1 line-clamp-2">
                            {exercise.description}
                            </p>
                        )}
                        <p className="text-xs text-muted-foreground mt-1">
                            {exercise.default_sets ?? emptyExercise.default_sets} sets ×{' '}
                            {exercise.default_rep_type === 'dynamic'
                            ? `${exercise.default_rep_min ?? emptyExercise.default_rep_min}-${exercise.default_rep_max ?? emptyExercise.default_rep_max}`
                            : (exercise.default_reps ?? emptyExercise.default_reps)}
                            {' '}reps •{' '}
                            {exercise.default_rest_time ?? emptyExercise.default_rest_time}s rest
                        </p>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-1 flex-shrink-0">
                        {exercise.isPublic ? (
                            <Button
                            variant="outline"
                            size="icon"
                            onClick={() => handleCopyPublicExercise(exercise)}
                            title="Copy to My Exercises"
                            className="h-8 w-8"
                            >
                                <Copy className="h-4 w-4" />
                                <span className="sr-only">Copy Exercise</span>
                            </Button>
                        ) : (
                            <>
                            <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => {
                                setCurrentExercise({ ...exercise });
                                setShowEditDialog(true);
                                }}
                                title="Edit Exercise"
                                className="h-8 w-8"
                            >
                                <Edit className="h-4 w-4" />
                                <span className="sr-only">Edit Exercise</span>
                            </Button>
                            <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => {
                                setCurrentExercise(exercise);
                                setShowDeleteDialog(true);
                                }}
                                title="Delete Exercise"
                                className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10"
                            >
                                <Trash2 className="h-4 w-4" />
                            <span className="sr-only">Delete Exercise</span>
                            </Button>
                            </>
                        )}
                    </div>
                </div>
                ))}
                 {/* --- END MAPPING --- */}
            </div>
          </ScrollArea>
        ) : (
          // *** Updated Empty State ***
          <div className="text-center py-12">
            <Dumbbell className="h-12 w-12 mx-auto text-muted-foreground opacity-30 mb-3" />
            <p className="text-muted-foreground font-medium">
              No exercises match your criteria.
            </p>
            <p className="text-sm text-muted-foreground mt-1">
              {searchQuery.trim() !== ''
                ? 'Try adjusting your search or filter.'
                : filterType === 'custom'
                ? 'You haven\'t added any custom exercises yet.'
                : filterType === 'public'
                ? 'No public exercises found in the library.'
                : 'Try adding a custom exercise.'}
            </p>
             {filterType !== 'custom' && ( // Show Add button only if not exclusively viewing custom
                 <Button variant="link" className="mt-2 text-sm" onClick={() => {
                     setCurrentExercise({ ...emptyExercise });
                     setShowAddDialog(true);
                 }}>
                    Add a Custom Exercise?
                 </Button>
             )}
          </div>
           // *** End Empty State ***
        )}
      </CardContent>

      {/* Add Exercise Dialog */}
      <Dialog open={showAddDialog} onOpenChange={(isOpen) => {
          setShowAddDialog(isOpen);
          if (!isOpen) setCurrentExercise(null);
      }}>
        <DialogContent className="sm:max-w-[525px]">
          <DialogHeader>
            <DialogTitle>Add Custom Exercise</DialogTitle>
            <DialogDescription>
              Define a new exercise for your personal library.
            </DialogDescription>
          </DialogHeader>
          {currentExercise && renderExerciseForm()}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button
              onClick={handleAddExercise}
              disabled={submitting || !currentExercise?.name?.trim()}
            >
              {submitting ? 'Adding...' : 'Add Exercise'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Exercise Dialog */}
      <Dialog open={showEditDialog} onOpenChange={(isOpen) => {
          setShowEditDialog(isOpen);
          if (!isOpen) setCurrentExercise(null);
      }}>
        <DialogContent className="sm:max-w-[525px]">
          <DialogHeader>
            <DialogTitle>Edit Exercise</DialogTitle>
            <DialogDescription>
              Update the details for "{currentExercise?.name}".
            </DialogDescription>
          </DialogHeader>
          {currentExercise && renderExerciseForm()}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button
              onClick={handleUpdateExercise}
              disabled={submitting || !currentExercise?.name?.trim()}
            >
              {submitting ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={(isOpen) => {
          setShowDeleteDialog(isOpen);
           if (!isOpen) setCurrentExercise(null);
      }}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Confirm Deletion</DialogTitle>
            <DialogDescription>
              Are you sure you want to permanently delete the exercise "{currentExercise?.name}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteExercise}
              disabled={submitting}
            >
              {submitting ? 'Deleting...' : 'Yes, Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default CustomExercisesManager;